// src/components/About.js
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  IoCodeSlashOutline,
  IoGlobeOutline,
  IoPeopleOutline,
  IoBarChartOutline,
  IoFitnessOutline,
  IoTrophyOutline,
  IoHeartOutline,
  IoCheckmarkCircleOutline,
  IoShieldCheckmarkOutline,
  IoKeyOutline,
} from 'react-icons/io5';
import { itemVariants } from './AnimatedPage';
import { useAuthContext } from '../context/AuthContext';
import api from '../api';

// ─── shared ──────────────────────────────────────────────────────────────────

const container = {
  animate: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};

const DevCard = () => (
  <motion.div
    variants={itemVariants}
    className="glass-panel rounded-2xl p-7 flex flex-col gap-4"
  >
    <div className="flex items-center gap-4">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-marizan-red/80 to-rose-700 flex items-center justify-center shadow-lg shadow-marizan-red/20">
        <IoCodeSlashOutline size={28} className="text-white" />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-0.5">Desarrollado por</p>
        <h2 className="text-xl font-bold text-white font-display">gZy.Code</h2>
      </div>
    </div>

    <a
      href="https://www.gzy92fs.com"
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 text-marizan-red hover:text-rose-400 transition-colors text-sm font-medium"
    >
      <IoGlobeOutline size={16} />
      www.gzy92fs.com
    </a>

    <p className="text-gray-400 text-sm leading-relaxed">
      Aplicación de fitness profesional diseñada para coaches y sus clientes.
      Seguimiento de entrenamientos, nutrición, logros y mucho más.
    </p>

    <div className="border-t border-white/8 pt-4 flex items-center justify-between text-xs text-gray-500">
      <span>© 2025 gZy.Code · Todos los derechos reservados</span>
      <span className="bg-white/5 px-2 py-0.5 rounded-full">v1.5</span>
    </div>
  </motion.div>
);

// ─── Coach view ───────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, color = 'text-white', delay = 0 }) => (
  <motion.div
    variants={itemVariants}
    className="glass-panel rounded-xl p-5 flex flex-col gap-2 card-lift"
  >
    <div className={`${color} opacity-80`}>
      <Icon size={22} />
    </div>
    <p className="text-2xl font-bold text-white font-display">{value ?? '—'}</p>
    <p className="text-xs text-gray-400 leading-tight">{label}</p>
  </motion.div>
);

const StatusDot = ({ ok }) => (
  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${ok ? 'bg-green-400' : 'bg-red-400'} animate-pulse`} />
);

const CoachAbout = () => {
  const [stats, setStats] = useState(null);
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/stats').catch(() => ({ data: null })),
      api.get('/license/status').catch(() => ({ data: null })),
    ])
      .then(([statsResponse, licenseResponse]) => {
        setStats(statsResponse.data);
        setLicense(licenseResponse.data);
      })
      .catch(() => {
        setStats(null);
        setLicense(null);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
      {/* Header */}
      <motion.div variants={itemVariants} className="text-center space-y-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-marizan-red">Panel del sistema</p>
        <h1 className="text-3xl font-bold text-white font-display">Acerca de la App</h1>
      </motion.div>

      {/* Health status */}
      <motion.div variants={itemVariants} className="glass-panel rounded-2xl p-5 space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-400 flex items-center gap-2">
          <IoShieldCheckmarkOutline size={15} /> Estado general
        </h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            { label: 'Servicio disponible', ok: true },
            { label: 'Datos sincronizados', ok: stats !== null },
            { label: 'Experiencia estable', ok: true },
            { label: 'Monitoreo activo', ok: true },
          ].map(({ label, ok }) => (
            <div key={label} className="flex items-center text-gray-300">
              <StatusDot ok={ok} />
              {label}
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div variants={itemVariants} className="glass-panel rounded-2xl p-5 space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-400 flex items-center gap-2">
          <IoKeyOutline size={15} /> Licencia de software
        </h3>
        {!license ? (
          <p className="text-sm text-gray-400">No se pudo consultar el estado de la licencia.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-white/5 p-4">
              <p className="text-xs uppercase tracking-widest text-gray-500">Licenciado a</p>
              <p className="mt-2 text-sm font-semibold text-white">{license.customer || 'Sin licencia activa'}</p>
            </div>
            <div className="rounded-xl bg-white/5 p-4">
              <p className="text-xs uppercase tracking-widest text-gray-500">Vence</p>
              <p className="mt-2 text-sm font-semibold text-white">{license.expires_at || 'Perpetua'}</p>
            </div>
            <div className="rounded-xl bg-white/5 p-4">
              <p className="text-xs uppercase tracking-widest text-gray-500">Dias restantes</p>
              <p className="mt-2 text-sm font-semibold text-white">
                {license.valid
                  ? (license.days_left == null ? 'Ilimitada' : `${license.days_left} dias`)
                  : '0 dias'}
              </p>
            </div>
          </div>
        )}
        {license?.reason ? <p className="text-xs text-gray-500">{license.reason}</p> : null}
      </motion.div>

      {/* Stats grid */}
      <motion.div variants={container} animate="animate" className="grid grid-cols-2 gap-3">
        <StatCard icon={IoPeopleOutline}   label="Clientes totales"   value={loading ? '…' : stats?.total_clients}  color="text-blue-400" />
        <StatCard icon={IoCheckmarkCircleOutline} label="Clientes activos" value={loading ? '…' : stats?.active_clients} color="text-green-400" />
        <StatCard icon={IoBarChartOutline} label="Programas"           value={loading ? '…' : stats?.total_programs} color="text-purple-400" />
        <StatCard icon={IoFitnessOutline}  label="Ejercicios"          value={loading ? '…' : stats?.total_exercises} color="text-yellow-400" />
        <StatCard icon={IoTrophyOutline}   label="Logros / Rewards"    value={loading ? '…' : stats?.total_rewards}  color="text-orange-400" />
        <StatCard icon={IoHeartOutline}    label="Sesiones registradas" value={loading ? '…' : stats?.total_logs}   color="text-rose-400" />
      </motion.div>

      {/* Privacy note */}
      <motion.div variants={itemVariants} className="glass-panel rounded-2xl p-5 space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-400 flex items-center gap-2">
          <IoShieldCheckmarkOutline size={15} /> Privacidad y seguridad
        </h3>
        <p className="text-sm text-gray-300 leading-relaxed">
          Esta sección muestra solo información funcional para operación diaria. Los detalles técnicos internos
          y de infraestructura no se exponen en la interfaz para proteger la seguridad de la plataforma.
        </p>
      </motion.div>

      {/* Short changelog */}
      <motion.div variants={itemVariants} className="glass-panel rounded-2xl p-5 space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-400 flex items-center gap-2">
          <IoCheckmarkCircleOutline size={15} /> Changelog corto v1.5
        </h3>
        <ul className="text-sm text-gray-300 space-y-2 list-disc pl-5">
          <li>Modo de rutinas secuencial/calendario en creacion y edicion de programas.</li>
          <li>Manuales de coach/cliente actualizados con los nuevos flujos.</li>
          <li>reCAPTCHA activo en login con validacion backend.</li>
          <li>Footer de marca y mejoras de visibilidad en pantallas de autenticacion.</li>
        </ul>
      </motion.div>

      {/* Dev card */}
      <DevCard />
    </div>
  );
};

// ─── Client view ──────────────────────────────────────────────────────────────

const ClientAbout = () => (
  <div className="container mx-auto px-4 py-8 max-w-lg space-y-6">
    <motion.div variants={itemVariants} className="text-center space-y-1">
      <p className="text-xs font-semibold uppercase tracking-widest text-marizan-red">Información</p>
      <h1 className="text-3xl font-bold text-white font-display">Acerca de</h1>
    </motion.div>

    <motion.div variants={itemVariants} className="glass-panel rounded-2xl p-6 text-center space-y-3">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-marizan-red/80 to-rose-700 flex items-center justify-center mx-auto shadow-lg shadow-marizan-red/20">
        <IoFitnessOutline size={30} className="text-white" />
      </div>
      <h2 className="text-xl font-bold text-white font-display">Marizan Fitness</h2>
      <p className="text-gray-400 text-sm leading-relaxed">
        Tu plataforma de fitness personal. Sigue tus entrenamientos, nutrición y progreso
        con la guía de tu coach.
      </p>
    </motion.div>

    <DevCard />
  </div>
);

// ─── Root export ──────────────────────────────────────────────────────────────

const About = () => {
  const { user } = useAuthContext();

  return (
    <motion.div
      variants={container}
      initial="initial"
      animate="animate"
    >
      {['coach', 'admin'].includes(user?.role) ? <CoachAbout /> : <ClientAbout />}
    </motion.div>
  );
};

export default About;
